import React, { useState } from 'react';
import useStore from '../utils/useStore';

const StickyNotes = ({ side = 'left' }) => {
  const [stickyNotes, setStickyNotes] = useStore([], `sticky-notes-${side}`);
  const [newNote, setNewNote] = useState('');
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const addNote = () => {
    if (newNote.trim() !== '') {
      const noteToAdd = {
        id: `note-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        text: newNote.trim(),
        createdAt: new Date().toISOString()
      };
      setStickyNotes([...stickyNotes, noteToAdd]);
      setNewNote('');
      setIsAddingNote(false);
    }
  };

  const deleteNote = (noteId) => {
    setStickyNotes(stickyNotes.filter(note => note.id !== noteId));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      addNote();
    } else if (e.key === 'Escape') {
      setNewNote('');
      setIsAddingNote(false);
    }
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <>
      {/* Desktop view - side panels */}
      <div 
        className={`fixed top-0 ${side}-0 w-80 h-full transition-all duration-300 ease-in-out hidden lg:block z-10 ${
          isExpanded 
            ? 'bg-gray-100/90 backdrop-blur-sm border-gray-300' 
            : 'bg-gray-50/50 backdrop-blur-lg'
        } border-${side === 'left' ? 'r' : 'l'} cursor-pointer`}
        onClick={!isExpanded ? toggleExpanded : undefined}
      >
        <div className={`h-full flex flex-col p-4 ${isExpanded ? '' : 'blur-sm'} transition-all duration-300`}>
          {/* Header */}
          <div className="mb-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                � Notes
              </h3>
              {isExpanded && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpanded();
                  }}
                  className="w-6 h-6 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded transition-colors"
                  title="Minimize"
                >
                  ×
                </button>
              )}
            </div>
            {isExpanded && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsAddingNote(!isAddingNote);
                }}
                className="w-full px-3 py-2 text-sm bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors text-gray-600 hover:text-gray-800"
              >
                + Add Note
              </button>
            )}
          </div>

          {/* Collapsed state indicator */}
          {!isExpanded && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-gray-500">
                <div className="text-2xl mb-2">💡</div>
                <div className="text-sm">Click to expand</div>
                {stickyNotes.length > 0 && (
                  <div className="text-xs mt-1 bg-gray-300 text-gray-700 rounded-full px-2 py-1 inline-block">
                    {stickyNotes.length} note{stickyNotes.length !== 1 ? 's' : ''}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Expanded content */}
          {isExpanded && (
            <>
              {/* Add new note form */}
              {isAddingNote && (
                <div className="mb-4 p-3 bg-gray-200 rounded-lg border border-gray-300">
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder="Type your note..."
                    className="w-full h-20 p-2 text-sm border border-gray-300 rounded resize-none focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-white"
                    autoFocus
                  />
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        addNote();
                      }}
                      className="px-3 py-1 text-xs bg-gray-500 hover:bg-gray-600 text-white rounded transition-colors"
                    >
                      Save
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setNewNote('');
                        setIsAddingNote(false);
                      }}
                      className="px-3 py-1 text-xs bg-gray-300 hover:bg-gray-400 text-gray-700 rounded transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Notes list */}
              <div className="flex-1 overflow-y-auto space-y-3">
                {stickyNotes.map((note) => (
                  <div
                    key={note.id}
                    className="p-3 bg-gray-200 rounded-lg border border-gray-300 shadow-sm group hover:shadow-md transition-shadow"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="text-sm text-gray-700 whitespace-pre-wrap mb-2">
                      {note.text}
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="text-xs text-gray-500">
                        {new Date(note.createdAt).toLocaleDateString()}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteNote(note.id);
                        }}
                        className="opacity-0 group-hover:opacity-100 w-6 h-6 flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-100 rounded transition-all"
                        title="Delete note"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
                
                {stickyNotes.length === 0 && !isAddingNote && (
                  <div className="text-center py-8 text-gray-400 text-sm">
                    No notes yet.<br />
                    Click "Add Note" to get started!
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Mobile view - only render if we're on the right side */}
      {side === 'right' && (
        <MobileStickyNotes />
      )}
    </>
  );
};

// Mobile component for horizontal scrollable notes
const MobileStickyNotes = () => {
  const [rightNotes] = useStore([], 'sticky-notes-right');
  const [isExpanded, setIsExpanded] = useState(false);

  const allNotes = rightNotes.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  return (
    <div className="lg:hidden fixed bottom-0 left-0 right-0 z-20">
      {/* Toggle button */}
      <div className="flex justify-center mb-2">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="bg-gray-100/90 backdrop-blur-sm px-4 py-2 rounded-t-lg border border-gray-300 border-b-0 text-gray-600 hover:text-gray-800 transition-colors"
        >
          <span className="text-sm font-medium">
            {isExpanded ? 'Hide Notes' : `Notes (${allNotes.length})`}
          </span>
          <span className="ml-2 text-xs">
            {isExpanded ? '▼' : '▲'}
          </span>
        </button>
      </div>

      {/* Notes panel */}
      {isExpanded && (
        <div className="bg-gray-100/95 backdrop-blur-sm border-t border-gray-300 p-4 max-h-48 overflow-y-auto">
          <div className="flex gap-3 overflow-x-auto pb-2">
            {allNotes.length === 0 ? (
              <div className="text-center w-full py-4 text-gray-500 text-sm">
                No notes yet. Use desktop view to add notes.
              </div>
            ) : (
              allNotes.map((note) => (
                <div
                  key={note.id}
                  className="flex-shrink-0 w-48 p-3 bg-gray-200 rounded-lg border border-gray-300 shadow-sm"
                >
                  <div className="text-sm text-gray-700 mb-2 line-clamp-3">
                    {note.text}
                  </div>
                  <div className="text-xs text-gray-500">
                    {new Date(note.createdAt).toLocaleDateString()}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StickyNotes;
